import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { KanbanTask, KanbanTaskStatus } from '../KanbanTaskStores';

/**
 * Mirrors `KanbanTaskRepository.GetKanbanTasks` (SkTech.Server/Repository/KanbanTaskRepository.cs,
 * ~lines 136-142):
 *
 *   var sevenDaysAgo = DateTime.UtcNow.AddDays(-7);
 *   .Where(t => !t.IsDeleted && t.ProjectId == projectId)
 *   .Where(t => !(t.Status != null && t.Status.IsFinalStatus && t.ModifiedDate <= sevenDaysAgo))
 *
 * i.e. a task drops off the kanban board once its status is a final status AND its
 * `ModifiedDate` is 7 or more days old. Note the server keys the cutoff off `ModifiedDate`
 * (last modification of the task or its comments), not `DueDate`/`CreatedDate`.
 */
export const BOARD_HIDE_FINAL_AFTER_DAYS = 7;

export function isVisibleOnBoard(
  task: KanbanTask,
  statuses: KanbanTaskStatus[],
  now: Dayjs = dayjs(),
): boolean {
  const status = task.status ?? statuses.find((s) => String(s.id) === String(task.statusId));
  if (!status?.isFinalStatus) {
    return true;
  }
  if (!task.modifiedDate) {
    return true;
  }
  // `modifiedDate` arrives from the API as an ISO string (the type says Dayjs, but the API client
  // never parses this field), so always wrap it; an unparsable value keeps the task visible.
  const modified = dayjs(task.modifiedDate as unknown as string | Dayjs);
  if (!modified.isValid()) {
    return true;
  }
  const cutoff = now.subtract(BOARD_HIDE_FINAL_AFTER_DAYS, 'day');
  // Server hides when `ModifiedDate <= sevenDaysAgo`, so visible is the strict complement.
  return modified.isAfter(cutoff);
}
